import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  Country,
  InstallationImp,
  InstallationsService,
  Product,
  Timezone,
} from '@ubie/core';
import { FeedbackService, FormHelper } from '@ubie/shared';

@Component({
  selector: 'acl-installation-form-v1',
  standalone: false,
  templateUrl: './installation-form-v1.component.html',
  styleUrl: './installation-form-v1.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationFormV1Component implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly installationsService = inject(InstallationsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly feedbackService = inject(FeedbackService);
  private readonly translate = inject(TranslateService);

  readonly canAddInstallation = input<boolean>(false);

  // disable the form
  readonly isFormDisabled = input<boolean>(false);

  // installation license
  readonly installationLicense = input<Product | undefined>(undefined);

  /**
   * Id of the installation to update
   * Leave blank if the form is used to add a new installation
   */

  readonly data = input<InstallationImp>();
  readonly install = input<InstallationImp>();
  readonly installationId = input<number>(0);
  readonly canShowButton = input<boolean>(false);
  readonly showNextPrevButtons = input<boolean>(false);
  readonly isInEditMode = computed(() => this.installationId() > 0);

  @Output() readonly installationDetailChange = new EventEmitter();
  @Output() readonly prevClicked = new EventEmitter();
  @Output() readonly nextWithData = new EventEmitter<InstallationImp>();

  form = this.formBuilder.group({});
  readonly btn_text = signal('installation_add');

  // RESOLVER VARS
  // ****************************
  // all timezones
  timezoneData = signal<Timezone[]>([]);
  // selected timezone in form
  selectedTimezone?: Timezone;
  // all country
  countriesData = signal<Country[]>([]);
  // selected Country in form
  selectedCountry?: Country;

  // FORM VARS
  // ****************************
  // installation form group
  installationForm: FormGroup;
  // installation object used to submit the data
  installation: InstallationImp | null = null;

  // FORM VALIDATION VARS
  // ****************************
  // helper for validation
  formHelper: FormHelper;
  // wether submit button was clicked
  isFormSubmitAttempted = signal(false);

  readonly isUpdating = signal(false);
  readonly isUpdateSuccess = signal(false);

  constructor() {
    this.installationForm = this.formBuilder.group({
      id: [],
      originId: [1],
      favorite: [false],
      name: ['', Validators.required],
      addressLine1: ['', Validators.required],
      postalCode: ['', Validators.required],
      city: ['', Validators.required],
      country: ['', Validators.required],
      timezone: ['', Validators.required],
    });

    effect(() => {
      if (this.isFormDisabled()) {
        this.installationForm?.disable();
      } else {
        this.installationForm?.enable();
      }
    });

    effect(() => {
      if (this.data()) {
        this.initForm();
      }
    });

    // init form helper for validation
    this.formHelper = new FormHelper(this.installationForm);
  }

  ngOnInit(): void {
    // get resolved data
    this.countriesData.set(this.route.snapshot?.data['countries']);
    this.timezoneData.set(this.route.snapshot?.data['timezones']);

    this.initForm();
  }

  initForm(): void {
    if (this.isInEditMode()) {
      this.btn_text.set('installation_btnUpdate');
    }

    // set selected installer
    this.selectedCountry = this.countriesData()?.find(
      (i) => i.id === this.data()?.country?.id,
    );
    this.selectedTimezone = this.timezoneData()?.find(
      (i) => i.id === this.data()?.timezone?.id,
    );

    // set form values
    this.installationForm?.patchValue(
      {
        id: this.data()?.id,
        originId: this.data()?.originId ?? 1,
        favorite: this.data()?.favorite,
        name: this.data()?.name,
        addressLine1: this.data()?.addressLine1,
        postalCode: this.data()?.postalCode,
        city: this.data()?.city,
        country: this.selectedCountry,
        timezone: this.selectedTimezone,
      },
      {
        emitEvent: false,
      },
    );
  }

  /**
   * reset form
   */
  resetForm(installationForm: FormGroup): void {
    installationForm.reset();
  }

  /**
   * Form is submitted
   */
  onSubmit(): void {
    this.isFormSubmitAttempted.set(true);
    if (this.installationForm?.valid) {
      if (this.isInEditMode()) {
        // update installation when in edit mode
        this.btn_text.set('installation_btnUpdate');
        this.updateInstallation();
      } else {
        this.btn_text.set('installation_add');
        this.insertInstallation();
      }
    }
  }

  private insertInstallation(): void {
    const license = this.installationLicense();

    if (license && this.installationForm) {
      // copy the form values over the object values
      const obj = { ...this.installation, ...this.installationForm.value };

      // add the license to the new installation
      const prod: Product = {
        name: license.name,
        uid: license.uid,
      };
      obj.license = prod;

      this.installationsService.addInstallation(obj).subscribe({
        next: (data) => {
          // details of the new installation are received
          //this.data = data;
          this.isFormSubmitAttempted.set(false);
          //reset form
          this.installationForm?.reset();
          // redirect to the installation detail

          this.router.navigate(['/installation/' + data.id + '/detail']);
        },
        error: (error) => {
          this.feedbackService.present(
            error.error.Message,
            5000,
            true,
            'warning',
          );
        },
      });
    }
  }

  private updateInstallation(): void {
    if (this.installationForm?.valid) {
      this.isUpdating.set(true);

      const objToSend = {
        ...this.data(),
        ...this.installationForm.value,
      };
      this.installationsService.updateInstallation(objToSend).subscribe({
        next: () => this.onUpdateSuccess(),
        error: (error) => this.onUpdateError(error),
      });
    }
  }

  private onUpdateSuccess(): void {
    // set updating bool to false: a checkmark will show instead of a loader
    this.isUpdating.set(false);
    this.isUpdateSuccess.set(true);

    this.isFormSubmitAttempted.set(false);

    this.feedbackService.present(
      this.translate.instant('installation_updateSuccess'),
      5000,
      true,
      'medium',
    );

    this.installationDetailChange.emit(this.installationForm?.value);

    setTimeout(() => {
      this.isUpdateSuccess.set(false);
    }, 2000);
  }

  private onUpdateError(error: any): void {
    // update failed
    this.isUpdateSuccess.set(false);
    // set updating bool to false: a checkmark will show instead of a loader
    this.isUpdating.set(false);

    const backendMessage = this.extractBackendMessage(error);

    this.feedbackService.present(
      backendMessage ?? 'installation_updateFail',
      5000,
      true,
      'danger',
    );
  }

  private extractBackendMessage(error: any): string | null {
    for (const candidate of [error?.error, error, error?.error?.error]) {
      const value =
        typeof candidate === 'string' ? this.safeJson(candidate) : candidate;

      const message = value?.Message ?? value?.message;
      if (this.isTranslationKey(message)) {
        return message;
      }
    }

    return null;
  }

  private safeJson(value: string): any | null {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private isTranslationKey(value: unknown): value is string {
    return (
      typeof value === 'string' &&
      /^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(value.trim())
    );
  }

  /**
   * button to go to previous page clicked
   */
  onPrevClicked(): void {
    this.prevClicked.emit();
  }

  /**
   * Emit form data to parent so the parent can proceed to the next step
   */
  onNextWithData(): void {
    this.isFormSubmitAttempted.set(true);
    if (this.installationForm?.valid) {
      const formValue = {
        ...this.installationForm.value,
        originId: Number(this.installationForm.value.originId),
      };
      const obj = Object.assign(
        new InstallationImp(),
        this.installation ?? {},
        formValue,
      );
      this.nextWithData.emit(obj);
    }
  }
}
